import type { Emotion } from "./catalog";
import { defaultPose, type Pose, type Vec } from "./pose";
import type { DwimSize } from "./theme";

export type IdleContext = {
  nowMs: number;
  dtMs: number;
  size: DwimSize;
  emotion: Emotion;
  reducedMotion: boolean;
  random: () => number;
};

type Tail = Vec & { rx: number; ry: number };

export type IdleState = {
  nextBlink: number;
  nextGlance: number;
  nextPeek: number;
  nextSkip: number;
  skipSide: number;
  tail1: Tail;
  tail2: Tail;
};

function clonePose(pose: Pose): Pose {
  return {
    ...pose,
    eyeL: { ...pose.eyeL },
    eyeR: { ...pose.eyeR },
    mouth: { ...pose.mouth },
    tail1: { ...pose.tail1 },
    tail2: { ...pose.tail2 },
    papers: pose.papers.map((p) => ({ ...p })),
    bubble: { ...pose.bubble },
  };
}

function cloneState(state: IdleState): IdleState {
  return {
    ...state,
    tail1: { ...state.tail1 },
    tail2: { ...state.tail2 },
  };
}

function isReclined(emotion: Emotion): boolean {
  return emotion === "sleeping" || emotion === "out";
}

function rotateAround(
  px: number,
  py: number,
  cx: number,
  cy: number,
  deg: number,
): Vec {
  const rad = (deg * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

function smoothTail(current: Tail, target: Tail, alpha: number): Tail {
  return {
    x: current.x + (target.x - current.x) * alpha,
    y: current.y + (target.y - current.y) * alpha,
    rx: current.rx + (target.rx - current.rx) * alpha,
    ry: current.ry + (target.ry - current.ry) * alpha,
  };
}

function tailTarget(orig: Tail, pose: Pose, origPose: Pose): Tail {
  const moved = {
    x: orig.x + (pose.x - origPose.x),
    y: orig.y + (pose.y - origPose.y),
    rx: orig.rx,
    ry: orig.ry,
  };
  const extra = pose.rotation - origPose.rotation;
  if (extra === 0) return moved;
  return { ...moved, ...rotateAround(moved.x, moved.y, pose.x, pose.y, extra) };
}

function opennessTriangle(elapsed: number): number {
  if (elapsed < 60) return 1 - elapsed / 60;
  return (elapsed - 60) / 60;
}

function pickSkipSide(random: () => number): number {
  const r = random();
  if (r < 1 / 3) return -1;
  if (r < 2 / 3) return 1;
  return 0;
}

export function createIdleState(nowMs: number, random: () => number): IdleState {
  const rest = defaultPose();
  return {
    nextBlink: nowMs + 2800 + random() * 2700,
    nextGlance: nowMs + 8000 + random() * 6000,
    nextPeek: nowMs + 20000 + random() * 10000,
    nextSkip: nowMs + 1400 + random() * 2200,
    skipSide: pickSkipSide(random),
    tail1: { ...rest.tail1 },
    tail2: { ...rest.tail2 },
  };
}

export function applyIdle(
  pose: Pose,
  state: IdleState,
  ctx: IdleContext,
): { pose: Pose; state: IdleState } {
  if (ctx.reducedMotion) return { pose, state };

  const next = cloneState(state);
  const out = clonePose(pose);
  const reclined = isReclined(ctx.emotion);
  const upright = !reclined;

  if (reclined) {
    out.scale = 1 + 0.03 * Math.sin(ctx.nowMs / 1600);
  } else {
    out.y += 3 * Math.sin(ctx.nowMs / 900);
  }

  if (ctx.emotion === "neutral") {
    out.mouth.curvature = pose.mouth.curvature + 0.4 * Math.sin(ctx.nowMs / 1100);
    out.mouth.width = pose.mouth.width + 0.9 * Math.sin(ctx.nowMs / 1300);
  }

  if (ctx.emotion === "bored") {
    out.rotation += 3 * Math.max(0, Math.sin(ctx.nowMs / 1900));
    out.eyeL.openness = Math.max(0.18, pose.eyeL.openness + 0.08 * Math.sin(ctx.nowMs / 1600));
    out.eyeR.openness = Math.max(0.12, pose.eyeR.openness + 0.06 * Math.sin(ctx.nowMs / 1600 + 0.7));
  }

  if (ctx.emotion === "happy") {
    const skipMs = 440;
    if (ctx.nowMs >= next.nextSkip && ctx.nowMs < next.nextSkip + skipMs) {
      const hop = Math.sin((Math.PI * (ctx.nowMs - next.nextSkip)) / skipMs);
      out.y -= 12 * hop;
      out.x += next.skipSide * 11 * hop;
      out.rotation += next.skipSide * 8 * hop;
    } else if (ctx.nowMs >= next.nextSkip + skipMs) {
      next.nextSkip = ctx.nowMs + 1600 + ctx.random() * 2600;
      next.skipSide = pickSkipSide(ctx.random);
    }
  }

  if (ctx.emotion === "yawning") {
    out.scale = 1 + 0.045 * Math.sin(ctx.nowMs / 480);
  }

  if (ctx.emotion === "sleeping") {
    const t = (ctx.nowMs / 1000) % 14;
    if (t >= 6 && t < 8) {
      out.mouth.kind = "smile";
      out.mouth.width = 16;
      out.mouth.curvature = 3.4;
      out.mouth.bias = 1.2;
    } else if (t >= 10 && t < 12.2) {
      out.mouth.kind = "o";
      out.mouth.width = 14;
      out.mouth.y = pose.mouth.y + 3;
    } else {
      out.mouth.curvature = pose.mouth.curvature + 0.35 * Math.sin(ctx.nowMs / 1200);
    }
  }

  if (upright) {
    if (ctx.nowMs >= next.nextBlink && ctx.nowMs < next.nextBlink + 120) {
      const openness = opennessTriangle(ctx.nowMs - next.nextBlink);
      out.eyeL.openness = openness;
      out.eyeR.openness = openness;
    } else if (ctx.nowMs >= next.nextBlink + 120) {
      next.nextBlink = ctx.nowMs + 2800 + ctx.random() * 2700;
    }

    if (ctx.emotion === "searching") {
      const scan = 2.8 * Math.sin(ctx.nowMs / 620);
      out.eyeL.pupilX = pose.eyeL.pupilX + scan;
      out.eyeR.pupilX = pose.eyeR.pupilX + scan;
    } else if (ctx.nowMs >= next.nextGlance && ctx.nowMs < next.nextGlance + 400) {
      const t = (ctx.nowMs - next.nextGlance) / 400;
      const a = Math.PI * t;
      out.eyeL.pupilX = pose.eyeL.pupilX + 1.3 * Math.sin(a);
      out.eyeR.pupilX = pose.eyeR.pupilX + 1.3 * Math.sin(a);
      out.eyeL.pupilY = pose.eyeL.pupilY + 1.0 * Math.cos(a);
      out.eyeR.pupilY = pose.eyeR.pupilY + 1.0 * Math.cos(a);
    } else if (ctx.nowMs >= next.nextGlance + 400) {
      next.nextGlance = ctx.nowMs + 8000 + ctx.random() * 6000;
    }

    if (ctx.size === "hero") {
      if (ctx.nowMs >= next.nextPeek && ctx.nowMs < next.nextPeek + 600) {
        const peekT = (ctx.nowMs - next.nextPeek) / 600;
        out.rotation += 12 * Math.sin(Math.PI * peekT);
      } else if (ctx.nowMs >= next.nextPeek + 600) {
        next.nextPeek = ctx.nowMs + 20000 + ctx.random() * 10000;
      }
    }
  }

  const alpha = 1 - Math.exp(-ctx.dtMs / 80);
  next.tail1 = smoothTail(next.tail1, tailTarget(pose.tail1, out, pose), alpha);
  next.tail2 = smoothTail(next.tail2, tailTarget(pose.tail2, out, pose), alpha);
  out.tail1 = { ...out.tail1, ...next.tail1 };
  out.tail2 = { ...out.tail2, ...next.tail2 };

  if (ctx.emotion === "happy") {
    const wag = 14 * Math.sin(ctx.nowMs / 150);
    out.tail2 = {
      ...out.tail2,
      ...rotateAround(out.tail2.x, out.tail2.y, out.tail1.x, out.tail1.y, wag),
    };
  }

  return { pose: out, state: next };
}
